import type { TQuotaPackage } from '../../services/models/BillingModels';

/**
 * Money and counts as the purchase form writes them. Pure: no store, no React.
 *
 * An amount is written by its currency's locale, not the reader's: a NOK amount the Norwegian way
 * (`1 500 kr`, `1 234,50 kr`) whatever language the form is in. The groups are joined by the
 * non-breaking space `Intl` emits, so an amount never wraps between them.
 */

interface ICurrencyWriting {
  locale: string;
  currencyDisplay: 'symbol' | 'code';
}

/** How each known currency is written. */
const CURRENCY_WRITING: Readonly<Record<string, ICurrencyWriting>> = {
  NOK: { locale: 'nb-NO', currencyDisplay: 'symbol' },
};

/** A currency the table does not know still gets an amount, by its code, so it cannot blank a card. */
const FALLBACK_WRITING: ICurrencyWriting = { locale: 'en-GB', currencyDisplay: 'code' };

/** The non-breaking space between a plain number and its code, as `Intl` puts one between an amount and its symbol. */
const NBSP = ' ';

/**
 * An amount alone, in its currency's way: no decimals when it is whole once rounded to two (so
 * `1234.999` is `1 235 kr`, not `1 235,00 kr`), otherwise exactly two.
 *
 * A code `Intl` does not accept as a currency (`''`, `'XX'`) never throws while a card renders: the
 * amount is then the plain grouped number in the fallback's way, followed by the code when there is one.
 */
export const formatAmount = (amount: number, currency: string): string => {
  const { locale, currencyDisplay } = CURRENCY_WRITING[currency] ?? FALLBACK_WRITING;
  const fractionDigits = Number.isInteger(Math.round(amount * 100) / 100) ? 0 : 2;
  const digits = { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits };
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay, ...digits }).format(amount);
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    const plain = new Intl.NumberFormat(FALLBACK_WRITING.locale, digits).format(amount);
    return currency === '' ? plain : `${plain}${NBSP}${currency}`;
  }
};

/** A package's price as the form shows it: the amount in its currency's way, excluding VAT. */
export const priceLabel = (aPackage: TQuotaPackage): string => `${formatAmount(aPackage.priceExVat, aPackage.currency)} excl. VAT`;

/** A count, such as a package's credits, grouped as the NOK amounts beside it (`1 000`), by the NOK row's locale. */
export const formatCount = (count: number): string => new Intl.NumberFormat(CURRENCY_WRITING.NOK.locale).format(count);

/** What one credit costs in a package, excluding VAT; null for a package with no credits, which has no unit price. */
const unitPrice = (aPackage: TQuotaPackage): number | null => (aPackage.credits > 0 ? aPackage.priceExVat / aPackage.credits : null);

/** What one credit costs in a package, as the card says it: `0,90 kr per credit`. Empty for a package with no credits. */
export const unitPriceLabel = (aPackage: TQuotaPackage): string => {
  const price = unitPrice(aPackage);
  return price === null ? '' : `${formatAmount(price, aPackage.currency)} per credit`;
};

/**
 * How much cheaper a credit is in `aPackage` than in the dearest package on sale, as a whole percent:
 * `Save 9%`. Null for the dearest package itself, for a saving that rounds to nothing, and for packages
 * priced in different currencies, whose credits cannot be compared.
 */
export const savingLabel = (aPackage: TQuotaPackage, packagesOnSale: TQuotaPackage[]): string | null => {
  const own = unitPrice(aPackage);
  if (own === null) return null;
  const comparable = packagesOnSale.filter((candidate) => candidate.currency === aPackage.currency);
  if (comparable.length !== packagesOnSale.length) return null;
  const dearest = Math.max(...comparable.map((candidate) => unitPrice(candidate) ?? 0));
  if (dearest <= 0) return null;
  const percent = Math.round((1 - own / dearest) * 100);
  return percent > 0 ? `Save ${percent}%` : null;
};
